import { Link, useLocation } from "react-router-dom";
import { Search, Map, Menu } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";

// Beach Umbrella SVG Logo
const BeachUmbrellaLogo = ({ className = "w-10 h-10" }) => (
  <svg viewBox="0 0 100 100" className={className}>
    {/* Blue circle background */}
    <circle cx="50" cy="50" r="48" fill="#0EA5E9" />
    {/* Umbrella top - colorful stripes */}
    <path d="M50 20 C25 20 15 45 15 45 L50 45 Z" fill="#EF4444" />
    <path d="M50 20 C55 20 62 28 67 45 L50 45 Z" fill="#FBBF24" />
    <path d="M67 45 C72 45 80 42 85 45 L50 45 C50 45 60 20 50 20 C60 20 75 25 85 45 Z" fill="#F97316" />
    <path d="M50 20 C40 20 30 28 23 45 L50 45 Z" fill="#FBBF24" />
    {/* Umbrella pole */}
    <rect x="48" y="45" width="4" height="30" fill="#8B5CF6" rx="1" />
    {/* Sand at bottom */}
    <ellipse cx="50" cy="78" rx="18" ry="6" fill="#FDE68A" />
    <ellipse cx="50" cy="78" rx="12" ry="4" fill="#FCD34D" />
  </svg>
);

const Navbar = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/search", label: "Find Beaches" },
    { href: "/map", label: "Map" },
    { href: "/blog", label: "Blog" },
  ];

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <nav 
      className="sticky top-0 z-50 bg-white shadow-sm"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="navbar-logo"
          >
            <BeachUmbrellaLogo className="w-11 h-11 transition-transform group-hover:scale-110" />
            <div className="hidden sm:block">
              <span className="font-extrabold text-xl text-[#0EA5E9] tracking-tight font-['Outfit']">
                PublicBeachAccess
              </span>
              <span className="text-[#F97316] font-bold">.com</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-200 ${
                  isActive(link.href)
                    ? "bg-[#0EA5E9] text-white shadow-md"
                    : "text-gray-700 hover:bg-[#FEF3C7] hover:text-[#F97316]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" className="text-[#0EA5E9]">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] bg-gradient-to-b from-sky-50 to-amber-50">
                <div className="flex flex-col gap-3 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                      className={`px-5 py-3 rounded-xl text-base font-bold transition-all duration-200 ${
                        isActive(link.href)
                          ? "bg-[#0EA5E9] text-white shadow-md"
                          : "text-gray-700 hover:bg-[#FEF3C7]"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
